"use client";

/**
 * Per-machine choice of which audio device playback goes to. Deliberately
 * NOT synced to the account like the language setting is — which speakers
 * or headphones exist is a property of this computer, not of the person,
 * so it lives in localStorage only.
 *
 * The real limit, stated up front rather than discovered by a support
 * ticket: `HTMLMediaElement.setSinkId()` and the output-picker it needs
 * (`navigator.mediaDevices.selectAudioOutput()`) are Chromium-only APIs —
 * no Safari, no Firefox, and, critically, no WebKit AT ALL. Tauri's
 * webview is WKWebView on macOS and WebKitGTK on Linux — both WebKit — so
 * on those two platforms this is unavailable inside the app today, same
 * as it is in Safari. It already works in Chrome/Edge in the browser
 * build, and will work in the desktop app once Windows ships (WebView2 is
 * Chromium). `sinkSelectionSupported()` is checked before any of this UI
 * renders, so the feature hides itself with an explanation instead of
 * pretending to work.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

// Not yet in TypeScript's DOM lib — MediaDeviceInfo, MediaDevices and
// HTMLMediaElement.setSinkId already are (checked against the installed
// typescript package), only the output-picker method is missing.
declare global {
  interface MediaDevices {
    selectAudioOutput?(options?: { deviceId?: string }): Promise<MediaDeviceInfo>;
  }
}

const STORAGE_KEY = "neodub_audio_sink";

export function sinkSelectionSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof HTMLMediaElement !== "undefined" &&
    "setSinkId" in HTMLMediaElement.prototype &&
    !!navigator.mediaDevices?.selectAudioOutput
  );
}

/** Point one media element at the chosen device. Exported for the one
 * place that creates <audio> elements outside the DOM tree — Step 3's
 * arrange board plays several tracks at once from a pool it manages
 * itself (components/dub/merge-arrange-panel.tsx) — the global observer
 * below only ever sees elements that actually enter the document. */
export function applySink(el: HTMLMediaElement, deviceId: string | null) {
  if (!deviceId) return; // null = system default, which is already where playback goes
  if (!("setSinkId" in el)) return;
  // A device unplugged since it was chosen fails here. Falling back to the
  // system default silently is correct — it is not an error about
  // anything the user just did.
  void (el as HTMLMediaElement & { setSinkId(id: string): Promise<void> })
    .setSinkId(deviceId)
    .catch(() => {});
}

interface StoredChoice {
  id: string;
  label: string;
}

interface AudioOutputValue {
  supported: boolean;
  deviceId: string | null;
  deviceLabel: string | null;
  choose: () => Promise<void>;
  reset: () => void;
}

const AudioOutputContext = createContext<AudioOutputValue>({
  supported: false,
  deviceId: null,
  deviceLabel: null,
  choose: async () => {},
  reset: () => {},
});

export function AudioOutputProvider({ children }: { children: React.ReactNode }) {
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [deviceLabel, setDeviceLabel] = useState<string | null>(null);
  // Starts false so server-rendered markup and the first client render
  // match — feature detection only means anything once we are in a real
  // browser, same reasoning as lib/i18n.tsx's language default.
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    setSupported(sinkSelectionSupported());
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const stored = JSON.parse(raw) as StoredChoice;
        setDeviceId(stored.id);
        setDeviceLabel(stored.label);
      }
    } catch {
      // malformed or inaccessible storage — stay on the system default
    }
  }, []);

  // Every <audio>/<video> that ever enters the document gets the current
  // device applied the moment it mounts — covers every player in the app
  // (main video/audio, waveform previews, voice designer sample, global
  // ref preview) without touching any of those components individually.
  // Re-runs on every deviceId change, which is what re-applies it to
  // elements that were already on the page before the choice was made.
  useEffect(() => {
    if (!deviceId) return;
    const applyWithin = (root: ParentNode) => {
      root.querySelectorAll?.("audio,video").forEach((el) => applySink(el as HTMLMediaElement, deviceId));
    };
    applyWithin(document);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches("audio,video")) applySink(node as HTMLMediaElement, deviceId);
          applyWithin(node);
        });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [deviceId]);

  const choose = useCallback(async () => {
    if (!navigator.mediaDevices?.selectAudioOutput) return;
    try {
      // The browser's own native picker — no enumerateDevices() + custom
      // list needed, and no microphone permission prompt either (that is
      // what enumerateDevices() would require just to get device labels).
      const device = await navigator.mediaDevices.selectAudioOutput();
      setDeviceId(device.deviceId);
      setDeviceLabel(device.label || null);
      try {
        window.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ id: device.deviceId, label: device.label } satisfies StoredChoice),
        );
      } catch {
        // storage disabled — the choice just does not persist
      }
    } catch {
      // the user dismissed the browser's picker — nothing changes
    }
  }, []);

  const reset = useCallback(() => {
    setDeviceId(null);
    setDeviceLabel(null);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo<AudioOutputValue>(
    () => ({ supported, deviceId, deviceLabel, choose, reset }),
    [supported, deviceId, deviceLabel, choose, reset],
  );

  return <AudioOutputContext.Provider value={value}>{children}</AudioOutputContext.Provider>;
}

export function useAudioOutput() {
  return useContext(AudioOutputContext);
}
